import React, { FC, memo, useMemo } from 'react';

import Decimal from 'decimal.js';

import Typography from '@mui/material/Typography';

import { useLiquidityChartData } from '@services/api/vaults/farms.api';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import Loader from '@components/common/Loader';

import LiquidityChart from '@pages/Vault/LiquidityChart';
import { ChartEntry } from '@pages/Vault/LiquidityChart/types/types';

import { CommonTokenDto } from '@dto/common/tokens.dto';

import { VaultFarmsType } from '@app-types/vaults/vault.types';
import { VaultFarmsV3ItemType } from '@app-types/vaults/vaultContext.types';

import useStyles from './styles.module';

interface Props {
  onResetState: () => void;
  onSetMinPercent: (value: Decimal) => void;
  onSetMaxPercent: (value: Decimal) => void;
  selectedV3Farm: VaultFarmsType;
  currentV3PoolInfo: VaultFarmsV3ItemType;
  minPercent: Decimal;
  maxPercent: Decimal;
  isPercentagesChanged: boolean;
  isEditable: boolean;
  isCustomMode: boolean;
  isInverseMode: boolean;
  tokens: {
    token0: CommonTokenDto;
    token1: CommonTokenDto;
  };
}

const TOKEN_DIVIDER = 10 ** 6;

const Chart: FC<Props> = ({
  onResetState,
  onSetMinPercent,
  onSetMaxPercent,
  selectedV3Farm,
  currentV3PoolInfo,
  minPercent,
  maxPercent,
  isPercentagesChanged,
  isCustomMode,
  isInverseMode,
  isEditable,
  tokens,
}) => {
  const { classes } = useStyles();

  const { data, isPending: isPendingChartData } = useLiquidityChartData(
    selectedV3Farm.farm.farmId,
  );

  const formattedData: ChartEntry[] = useMemo(
    () =>
      data
        ? data.chart.map((item) => ({
            liquidityUsd: Number(item.liquidityUsd),
            price: isInverseMode
              ? toBN(1).div(item.price).toNumber()
              : Number(item.price),
          }))
        : [],
    [data, isInverseMode],
  );

  const feeAmount = useMemo(
    () => currentV3PoolInfo.poolV3Fee * TOKEN_DIVIDER,
    [currentV3PoolInfo.poolV3Fee],
  );

  const marketPrice = useMemo(
    () =>
      isInverseMode
        ? currentV3PoolInfo?.priceToken0PerToken1.equals(0)
          ? toBN(0)
          : toBN(1).div(currentV3PoolInfo?.priceToken0PerToken1)
        : currentV3PoolInfo?.priceToken0PerToken1,
    [currentV3PoolInfo, isInverseMode],
  );

  const currentPrice = useMemo(() => {
    if (!data?.currentPrice) return 0;

    return isInverseMode
      ? toBN(1).div(data.currentPrice).toNumber()
      : Number(data?.currentPrice);
  }, [data, isInverseMode]);

  return (
    <div className={classes.chatBox}>
      {isPendingChartData ? (
        <Loader size={32} isCenter />
      ) : formattedData?.length ? (
        <div className={classes.chat}>
          <div className={classes.chatHeader}>
            <div className={classes.marketPrice}>
              <Typography variant="body2" color={(theme) => theme.secondary.s3}>
                Market price
              </Typography>
              <Typography variant="body2" color={(theme) => theme.secondary.s3}>
                <span className={classes.price}>
                  {formatBNWithCommas(marketPrice || 0, 6, 0, true)}
                </span>
                &nbsp;
                {tokens.token1.symbol} per {tokens.token0.symbol}
              </Typography>
            </div>
          </div>

          <LiquidityChart
            price={currentPrice}
            feeAmount={feeAmount}
            formattedData={formattedData}
            minPercent={minPercent}
            maxPercent={maxPercent}
            interactive={isEditable && isCustomMode}
            onResetState={onResetState}
            onSetMinPercent={onSetMinPercent}
            onSetMaxPercent={onSetMaxPercent}
            isResetDisabled={!isPercentagesChanged}
          />
        </div>
      ) : (
        <Typography variant="h4">Liquidity data not available.</Typography>
      )}
    </div>
  );
};

export default memo(Chart);
